/**
 * Book-of-business summary helpers for the Customer Success cockpit.
 *
 * These power the "3-second read" header band and the one-glance row lead:
 *   - `summarizeBook`   — average health, tier counts, and ARR at risk over
 *                         the ALREADY-scored rows (no re-scoring, no drift);
 *   - `biggestNegativeFactor` — the single heaviest debit among the signed
 *                         contributors the explainability layer already
 *                         computed (reused, never recomputed);
 *   - `filterByBand`    — the tier-chip filter over scored rows;
 *   - `weeklyUpdatesDueCount` — how many accounts have a resolvable
 *                         recipient, i.e. a weekly update that can actually
 *                         send (reuses the one canonical resolver).
 *
 * Deliberately PURE: every function reads only its arguments. An empty book
 * yields honest empties (`avgScore: null`, zero counts, $0 at risk) — never
 * a fabricated zero average.
 */

import type { Account, Person } from '../db/types';
import type { CustomerHealth, HealthBand } from './health';
import { resolveWeeklyUpdateRecipient } from './weeklyUpdate';

/** One already-scored customer, as the view holds it. */
export interface BookRow {
  health: CustomerHealth;
  /** The account's own annual value; omit when unset (never invented). */
  arr?: number;
}

export interface BookSummary {
  /** Live customers in the book. */
  total: number;
  /** Mean health score, rounded — `null` for an empty book (honest, not 0). */
  avgScore: number | null;
  /** Customers per tier. */
  counts: Record<HealthBand, number>;
  /** Sum of ARR across at-risk customers; accounts without ARR add nothing. */
  arrAtRisk: number;
}

/** Roll the scored book up into the header-band numbers. */
export function summarizeBook(rows: readonly BookRow[]): BookSummary {
  const counts: Record<HealthBand, number> = { 'at-risk': 0, watch: 0, healthy: 0 };
  let scoreSum = 0;
  let arrAtRisk = 0;
  for (const row of rows) {
    counts[row.health.band]++;
    scoreSum += row.health.score;
    if (row.health.band === 'at-risk' && typeof row.arr === 'number') arrAtRisk += row.arr;
  }
  return {
    total: rows.length,
    avgScore: rows.length > 0 ? Math.round(scoreSum / rows.length) : null,
    counts,
    arrAtRisk,
  };
}

/** A signed contributor — fits both `HealthFactor.points` and driver deltas. */
export interface SignedFactor {
  label: string;
  /** Signed points; debits are negative. */
  points: number;
}

/**
 * The single heaviest debit among already-computed signed contributors —
 * the row's one-line "biggest factor". Returns `null` when nothing is
 * negative (an honestly clean account), never a fabricated culprit.
 * Ties keep the first contributor (the layer's own display order).
 */
export function biggestNegativeFactor(
  factors: readonly SignedFactor[],
): SignedFactor | null {
  let worst: SignedFactor | null = null;
  for (const f of factors) {
    if (f.points >= 0) continue;
    if (worst === null || f.points < worst.points) worst = f;
  }
  return worst;
}

/** Tier-chip filter: `null` band means "show everything". */
export function filterByBand<T extends { health: Pick<CustomerHealth, 'band'> }>(
  rows: readonly T[],
  band: HealthBand | null,
): T[] {
  return band === null ? [...rows] : rows.filter((r) => r.health.band === band);
}

/**
 * Accounts whose weekly update can actually send this week — i.e. a
 * recipient email resolves somewhere on the record (the same canonical
 * resolver the send pipeline uses). Accounts with no address are not
 * counted "due": they cannot send, and the panel says so honestly.
 */
export function weeklyUpdatesDueCount(
  accounts: readonly Account[],
  people: readonly Person[],
): number {
  return accounts.filter((a) => resolveWeeklyUpdateRecipient(a, [...people]) !== null).length;
}
